import { useState, type TableHTMLAttributes } from "react";
import { useTranslations } from "use-intl";
import dayjs from "dayjs";
import { Button } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { CircleNotchIcon } from "@phosphor-icons/react";
import { getDefaultClassNames } from "react-day-picker";
import { tripAvailabilityQueryOptions } from "@/app/lib/hooks/useTripAvailability";
import { getDateString } from "@/app/lib/utils/dates";
import { SwipeableDrawer } from "@/app/ui/SwipeableDrawer";
import { CalendarBase } from "@/app/ui/calendar/CalendarBase";

const MonthGridWithLoader = (props: TableHTMLAttributes<HTMLTableElement>) => (
  <>
    <table {...props} />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 rounded-2xl bg-surface-background p-4">
      <CircleNotchIcon className="animate-spin" size={30} />
    </div>
  </>
);

type CalendarDrawerContentsProps = {
  tripId: string;
  selectedDate: string | null;
  startMonth: string;
  endMonth: string;
  selectedMonth: string;
  onConfirm: (tripDate: string | null) => void;
};

export function CalendarDrawerContents({
  tripId,
  selectedDate,
  startMonth,
  endMonth,
  selectedMonth,
  onConfirm,
}: CalendarDrawerContentsProps) {
  const t = useTranslations();

  const [localMonthValue, setLocalMonthValue] = useState(selectedMonth);
  const [localTripValue, setLocalTripValue] = useState(selectedDate);

  const { data: availabilityData, isPending } = useQuery({
    ...tripAvailabilityQueryOptions(tripId, localMonthValue),
    select: (response) => {
      const { availability } = response.data.data;
      return new Set(availability.map((item) => item.date));
    },
  });
  const defaultClassNames = getDefaultClassNames();

  const calendarComponent = (
    <CalendarBase
      mode="single"
      required
      selected={localTripValue ? new Date(localTripValue) : undefined}
      onSelect={(date) => {
        setLocalTripValue(getDateString(date));
      }}
      disabled={(date) => !availabilityData?.has(getDateString(date))}
      defaultMonth={new Date(localMonthValue)}
      startMonth={new Date(startMonth)}
      endMonth={new Date(endMonth)}
      onMonthChange={(month) => {
        setLocalMonthValue(dayjs(month).format("YYYY-MM"));
      }}
      components={isPending ? { MonthGrid: MonthGridWithLoader } : undefined}
      classNames={{
        today: "border-transparent",
        day_button: `${defaultClassNames.day_button} enabled:border-stroke-neutral-medium! enabled:border!`,
      }}
    />
  );

  return (
    <>
      <h2 className="mt-4 mb-6 text-xl font-medium text-text-title">
        {t("trips.dateSelection.selectStartDate")}
      </h2>

      <div className="mb-6 flex items-center justify-center">
        {calendarComponent}
      </div>

      <Button
        variant="contained"
        onClick={() => {
          onConfirm(localTripValue);
        }}
        fullWidth
      >
        {t("confirm")}
      </Button>
    </>
  );
}

type Props = CalendarDrawerContentsProps & {
  open: boolean;
  onClose: () => void;
};

export function CalendarDrawer({ open, onClose, ...props }: Props) {
  return (
    <SwipeableDrawer open={open} onClose={onClose}>
      {/* Reinitialize internal state on each open */}
      {open ? <CalendarDrawerContents {...props} /> : null}
    </SwipeableDrawer>
  );
}
